// Lógica pura sobre el informe de una publicación (el real o el de la vista previa): listas planas de
// `successes`, `failures` y `skipped`, cada entrada con su `version`, `filename` y `client`.

export const failureText = (failure) => [failure.reason, failure.missing?.join(', '),
    failure.published_before_error?.length > 0 && `(ya quedaron publicados: ${failure.published_before_error.join(', ')})`].filter(Boolean).join(' ');

// Junta los clientes que comparten el mismo texto: [{ text, clients }], en el orden en que aparecen.
const groupByText = (entries) => {
    const groups = [];
    entries.forEach(([text, client]) => {
        const group = groups.find(g => g.text === text) || groups[groups.push({ text, clients: [] }) - 1];
        if (client) group.clients.push(client);
    });
    return groups;
};

// Una falla con `not_applicable` no es una falla: el update es de otro cliente (`owner`) y a éste no le corresponde.
// Lo marca el servidor, que es quien aplica la regla de dueño; acá sólo se separa lo esperado de lo que salió mal.
export const splitFailures = (failures = []) => ({
    expected: failures.filter(failure => failure.not_applicable),
    failed: failures.filter(failure => !failure.not_applicable),
});

// "31 clientes elegidos no lo reciben": lo esperado, dicho corto. Con varios updates se cuentan los casos.
export const notReceiving = (count, fileCount, done = false) => fileCount > 1
    ? `${count} no ${count === 1 ? 'corresponde' : 'corresponden'}`
    : `${count} ${count === 1 ? `cliente elegido no lo ${done ? 'recibió' : 'recibe'}` : `clientes elegidos no lo ${done ? 'recibieron' : 'reciben'}`}`;

// Detalle de UNA versión: por update, a quién va, quién ya lo tiene, a quién no le corresponde, a quién no se le
// publica y por qué.
export const versionBreakdown = (report, version, filenames) => {
    const inVersion = (list) => (list || []).filter(entry => entry.version === version);
    const files = filenames.map(filename => {
        const mine = (list) => inVersion(list).filter(entry => entry.filename === filename);
        const skipped = mine(report.skipped);
        const { expected, failed } = splitFailures(mine(report.failures));
        return {
            filename,
            published: mine(report.successes).map(entry => entry.client),
            alreadyPublished: skipped.filter(entry => entry.status !== 'ignored').map(entry => entry.client),
            ignored: skipped.filter(entry => entry.status === 'ignored').map(entry => entry.client),
            notApplicable: expected.length > 0 ? { owner: expected[0].owner, clients: expected.map(failure => failure.client) } : null,
            failures: groupByText(failed.map(failure => [failureText(failure), failure.client])),
        };
    });
    const dependencies = groupByText(Object.entries(report.published_dependencies?.[version] || {}).map(([client, names]) => [names.join(', '), client]));
    const { expected, failed } = splitFailures(inVersion(report.failures));
    return { files, dependencies, published: inVersion(report.successes).length, notApplicable: expected.length, failed: failed.length };
};

// Prerrequisitos que le faltan a algún cliente en esas versiones (cada versión tiene los suyos), en orden de
// publicación: primero las cadenas que se liberarían con dependencias, después lo que se sabe que falta sin ellas.
export const missingInVersions = (preview, versions) => {
    const names = new Set();
    versions.forEach(version => Object.values(preview.with_dependencies.published_dependencies?.[version] || {})
        .forEach(chain => chain.forEach(name => names.add(name))));
    (preview.plain.failures || []).filter(failure => versions.includes(failure.version))
        .forEach(failure => (failure.missing || []).forEach(name => names.add(name)));
    return [...names];
};
